import Layout from '@/components/Layout'
import { useState, useEffect, useMemo } from 'react'
import './style.css'

/**
 * MessageReport Component
 * 
 * Page for viewing message reports with filters and export options.
 */
function MessageReport() {
  const [entriesPerPage, setEntriesPerPage] = useState(50)
  const [searchValue, setSearchValue] = useState('')
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 7)
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [toDate, setToDate] = useState(() => {
    const d = new Date()
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
  })
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Sample data - empty initially to show "No Record Found"
  useEffect(() => {
    // Empty data to match the image
    setTableData([])
  }, [])

  // Filter and paginate data
  const filteredData = useMemo(() => {
    let filtered = tableData

    if (searchValue) {
      filtered = filtered.filter(
        (item) =>
          item.userName?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.eventName?.toLowerCase().includes(searchValue.toLowerCase()) ||
          item.remark?.toLowerCase().includes(searchValue.toLowerCase())
      )
    }

    return filtered
  }, [tableData, searchValue])

  const totalPages = Math.max(1, Math.ceil(filteredData.length / entriesPerPage))
  const startIndex = (currentPage - 1) * entriesPerPage
  const endIndex = startIndex + entriesPerPage
  const paginatedData = filteredData.slice(startIndex, endIndex)

  // Handle Load button click
  const handleLoad = async () => {
    setLoading(true)
    try {
      // TODO: Replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 500))
      // API call would go here
    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePageChange = (page) => {
    setCurrentPage(page)
  }

  return (
    <Layout>
      <div className="w-full bg-[#171b36] min-h-screen p-4">
        {/* Header Section */}
        <div className="shadow-sm">
          <div className="flex justify-between items-start mb-2">
            <div
              style={{ color: "#cae0e8", fontSize: "24px" }}
              className="font-semibold"
            >
              Message Report
            </div>
          </div>
          <div className="w-full h-px bg-white mb-2"></div>
        </div>

        {/* Filter Section */}
        <div className="mb-4 rounded-sm">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <label className="text-white text-sm mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex flex-col">
              <label className="text-white text-sm mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="px-3 py-2 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={handleLoad}
              disabled={loading}
              className="px-6 py-2 bg-[#333d79] text-white font-medium rounded-sm hover:bg-[#2d3569] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Loading...' : 'Load'}
            </button>
          </div>
        </div>

        {/* Table Controls */}
        <div className="mb-4 rounded-sm">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <span className="text-white text-sm font-bold">Show</span>
              <select
                value={entriesPerPage}
                onChange={(e) => {
                  setEntriesPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                className="px-2 py-1 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
                <option value="500">500</option>
              </select>
              <span className="text-white text-sm font-bold">entries</span>
            </div>

            <div className="flex items-center gap-2">
              <label className="text-white text-sm font-bold">Search:</label>
              <input
                type="text"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value)
                  setCurrentPage(1)
                }}
                placeholder="Type to Search"
                className="px-3 py-1 bg-white text-black rounded-sm border-none focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder-gray-500"
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="shadow-sm py-2">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    file
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    User Name
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Event Name
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Event Date
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Remark
                  </th>
                  <th className="text-left px-2 py-1 text-white font-bold text-[14px]">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" className="px-2 py-8 text-center text-gray-400">
                      Loading...
                    </td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr style={{ backgroundColor: "rgba(0, 0, 0, 0.4)", padding: "10px" }}>
                    <td colSpan="6" className="px-2 py-2 text-center text-white">
                      No Record Found.
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((row, index) => (
                    <tr
                      key={row.id}
                      className={`transition-colors ${
                        index % 2 === 0 ? "bg-[rgba(245,245,245,0.1)]" : ""
                      }`}
                    >
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.file || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.userName || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.eventName || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.eventDate || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.remark || '-'}
                      </td>
                      <td className="py-1 px-2 text-white text-[13px]">
                        {row.amount || '-'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="my-1 p-m-l col">
              <ul
                role="menubar"
                aria-disabled="false"
                aria-label="Pagination"
                className="pagination my-0 b-pagination justify-content-end"
              >
                <li
                  role="presentation"
                  aria-hidden={currentPage === 1 ? "true" : "false"}
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  {currentPage === 1 ? (
                    <span
                      role="menuitem"
                      aria-label="Go to first page"
                      aria-disabled="true"
                      className="page-link"
                    >
                      «
                    </span>
                  ) : (
                    <button
                      role="menuitem"
                      type="button"
                      aria-label="Go to first page"
                      onClick={() => setCurrentPage(1)}
                      className="page-link"
                    >
                      «
                    </button>
                  )}
                </li>
                <li
                  role="presentation"
                  aria-hidden={currentPage === 1 ? "true" : "false"}
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  {currentPage === 1 ? (
                    <span
                      role="menuitem"
                      aria-label="Go to previous page"
                      aria-disabled="true"
                      className="page-link"
                    >
                      ‹
                    </span>
                  ) : (
                    <button
                      role="menuitem"
                      type="button"
                      aria-label="Go to previous page"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      className="page-link"
                    >
                      ‹
                    </button>
                  )}
                </li>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <li
                    key={page}
                    role="presentation"
                    className={`page-item ${currentPage === page ? "active" : ""}`}
                  >
                    <button
                      role="menuitemradio"
                      type="button"
                      aria-label={`Go to page ${page}`}
                      aria-checked={currentPage === page}
                      aria-posinset={page}
                      aria-setsize={totalPages}
                      tabIndex={currentPage === page ? 0 : -1}
                      onClick={() => setCurrentPage(page)}
                      className="page-link"
                    >
                      {page}
                    </button>
                  </li>
                ))}
                <li
                  role="presentation"
                  aria-hidden={currentPage === totalPages ? "true" : "false"}
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  {currentPage === totalPages ? (
                    <span
                      role="menuitem"
                      aria-label="Go to next page"
                      aria-disabled="true"
                      className="page-link"
                    >
                      ›
                    </span>
                  ) : (
                    <button
                      role="menuitem"
                      type="button"
                      aria-label="Go to next page"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      className="page-link"
                    >
                      ›
                    </button>
                  )}
                </li>
                <li
                  role="presentation"
                  aria-hidden={currentPage === totalPages ? "true" : "false"}
                  className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
                >
                  {currentPage === totalPages ? (
                    <span
                      role="menuitem"
                      aria-label="Go to last page"
                      aria-disabled="true"
                      className="page-link"
                    >
                      »
                    </span>
                  ) : (
                    <button
                      role="menuitem"
                      type="button"
                      aria-label="Go to last page"
                      onClick={() => setCurrentPage(totalPages)}
                      className="page-link"
                    >
                      »
                    </button>
                  )}
                </li>
              </ul>
            </div>
        </div>
      </div>
    </Layout>
  )
}

export default MessageReport

